import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { AvatarPicker } from "@/components/AvatarPicker";
import { JButton } from "@/components/JButton";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useSmartBack } from "@/hooks/use-smart-back";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/child/$childId/edit")({
  component: ChildEdit,
});

function ChildEdit() {
  const { childId } = Route.useParams();
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/child/$childId", params: { childId } }));
  const [name, setName] = useState("");
  const [age, setAge] = useState(6);
  const [emoji, setEmoji] = useState("🌱");
  const [color, setColor] = useState("primary");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("children")
        .select("name, age, avatar_emoji, accent_color")
        .eq("id", childId)
        .single();
      if (data) {
        setName(data.name);
        setAge(data.age);
        setEmoji(data.avatar_emoji);
        setColor(data.accent_color);
      }
    })();
  }, [childId]);

  const ageTier: "4-6" | "7-9" | "10-12" = age <= 6 ? "4-6" : age <= 9 ? "7-9" : "10-12";

  const save = async () => {
    if (!name.trim()) return toast.error(isAr ? "أدخل الاسم" : "Enter a name");
    setBusy(true);
    const { error } = await supabase
      .from("children")
      .update({ name: name.trim(), age, avatar_emoji: emoji, accent_color: color, age_tier: ageTier })
      .eq("id", childId);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(isAr ? "تم الحفظ" : "Saved");
    navigate({ to: "/app/child/$childId", params: { childId } });
  };

  const remove = async () => {
    if (!confirm(isAr ? "حذف هذا الملف؟" : "Delete this profile?")) return;
    setBusy(true);
    const { error } = await supabase.from("children").delete().eq("id", childId);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(isAr ? "تم الحذف" : "Deleted");
    navigate({ to: "/app" });
  };

  return (
    <div dir={dir} className="px-5 py-6 max-w-md mx-auto animate-fade-in">
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> {isAr ? "رجوع" : "Back"}
      </button>

      <h1 className="font-display-en text-2xl font-bold mb-1">{isAr ? "تعديل الملف" : "Edit profile"}</h1>
      <p className="text-sm text-foreground/60 mb-6">{isAr ? "اجعل الملف خاصًا" : "Make it personal"}</p>

      <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5 text-center">
        <div className={`mx-auto size-24 rounded-3xl bg-${color}/20 flex items-center justify-center text-5xl mb-2 animate-scale-in`}>
          {emoji}
        </div>
        <p className="font-bold text-lg">{name || (isAr ? "اسم الطفل" : "Child name")}</p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-foreground/60">
            {isAr ? "الاسم" : "Name"}
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-foreground/60">
            {isAr ? "العمر" : "Age"}: {age}
          </label>
          <input
            type="range"
            min={4}
            max={12}
            value={age}
            onChange={(e) => setAge(parseInt(e.target.value))}
            className="w-full mt-2 accent-primary"
          />
          <p className="text-xs text-foreground/60 mt-1 uppercase tracking-wider">{ageTier}</p>
        </div>
        <AvatarPicker emoji={emoji} color={color} onEmoji={setEmoji} onColor={setColor} />
      </div>

      <JButton onClick={save} disabled={busy} className="w-full mb-3" size="lg">
        {busy ? "…" : isAr ? "حفظ" : "Save"}
      </JButton>

      <button
        onClick={remove}
        disabled={busy}
        className="w-full py-3 rounded-2xl text-sm font-bold text-destructive hover:bg-destructive/10 transition-colors flex items-center justify-center gap-2"
      >
        <Trash2 className="size-4" /> {isAr ? "حذف الملف" : "Delete profile"}
      </button>
    </div>
  );
}
